/**
 * @file CartModeGroup.tsx
 * @description Группа «Вид корзины» (phase19.16, SB120 этап 4; uiux-cart §4–§5):
 *
 * ```
 * ┌──────────┐┌──────────┐   OptionCards схемами
 * │▔▔▔▔▔▔[К] ││▔▔▔▔▔▔▔▔▔▔│
 * │    ┌──┐  ││      ┌───┤
 * │Окно из   ││Панель  ✓ │
 * └──────────┘└──────────┘
 * Сторона  [ Слева | Справа ]          ← только у панели
 * ```
 * У окна вместо стороны — строка «На телефоне окно открывается панелью
 * справа шириной «на телефоне»» (окно всегда у правого края рамки сайта).
 */

import { SITE_CART_MODE_LABELS, SITE_CART_SIDE_LABELS, SITE_CART_SIDES, type SiteCartMode } from "@chaiberi/shared";

import { Label } from "../../ui/label";
import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { useCartLook } from "./cart-look-context";
import { CartFields } from "./CartFields";
import { CartModeScheme } from "./CartModeScheme";
import { CART_POPOVER_PHONE_NOTE, groupHasWarning, modeSummary } from "../../../lib/site-view/cart-look";

/** Порядок схем — как в проекте: окно, затем панель. */
const MODES: ReadonlyArray<{ value: SiteCartMode; hint: string }> = [
  { value: "popover", hint: "Под значком корзины в шапке" },
  { value: "drawer", hint: "Во всю высоту экрана сбоку" },
];

export function CartModeGroup() {
  const { cart, warnings, change } = useCartLook();
  return (
    <SettingsGroup
      id="mode"
      title="Вид корзины"
      tocLabel="Вид"
      hint="Как покупатель видит корзину."
      summary={modeSummary(cart)}
      status={groupHasWarning(warnings, "mode") ? "warning" : undefined}
    >
      <CartFields>
        <OptionCards
          aria-label="Вид корзины"
          columns={2}
          value={cart.mode}
          onChange={(mode) => change({ mode })}
          options={MODES.map((m) => ({ ...m, label: SITE_CART_MODE_LABELS[m.value], preview: <CartModeScheme mode={m.value} side={cart.drawer.side} /> }))}
        />
        {cart.mode === "drawer" ? (
          <div className="flex flex-col gap-2">
            <Label>Сторона</Label>
            <Segmented
              aria-label="Сторона панели"
              options={SITE_CART_SIDES.map((value) => ({ value, label: SITE_CART_SIDE_LABELS[value] }))}
              value={cart.drawer.side}
              onChange={(side) => change({ drawer: { ...cart.drawer, side } })}
            />
          </div>
        ) : (
          <FieldHint className="mt-0">{CART_POPOVER_PHONE_NOTE}</FieldHint>
        )}
      </CartFields>
    </SettingsGroup>
  );
}
